import { useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { FileText, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
} from '@/features/files/types/file.types';

interface SignedDeclarationDropzoneProps {
  onUpload: (files: File[]) => Promise<boolean>;
  isUploading?: boolean;
  className?: string;
}

export function SignedDeclarationDropzone({
  onUpload,
  isUploading = false,
  className,
}: SignedDeclarationDropzoneProps) {
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      rejectedFiles.forEach((rejection) => {
        const errors = rejection.errors.map((e) => e.message).join(', ');
        toast.error(`"${rejection.file.name}": ${errors}`);
      });

      if (acceptedFiles.length === 0 || isUploading) return;

      await onUpload(acceptedFiles);
    },
    [onUpload, isUploading]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: ALLOWED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled: isUploading,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 sm:p-8 transition-all',
        isUploading ? 'cursor-wait opacity-70' : 'cursor-pointer',
        isDragActive && !isDragReject && 'border-primary bg-primary/5 scale-[1.01]',
        isDragReject && 'border-destructive bg-destructive/5',
        !isDragActive && 'border-border hover:border-primary/50 hover:bg-accent/30',
        className
      )}
    >
      <input {...getInputProps()} />

      <div className="flex flex-col items-center gap-3 sm:gap-4 text-center">
        <div
          className={cn(
            'rounded-full p-3 sm:p-4 transition-colors',
            isDragActive && !isDragReject && 'bg-primary/10 text-primary',
            isDragReject && 'bg-destructive/10 text-destructive',
            !isDragActive && 'bg-muted text-muted-foreground'
          )}
        >
          {isUploading ? (
            <Spinner className="h-6 w-6 sm:h-8 sm:w-8" />
          ) : isDragReject ? (
            <X className="h-6 w-6 sm:h-8 sm:w-8" />
          ) : isDragActive ? (
            <FileText className="h-6 w-6 sm:h-8 sm:w-8" />
          ) : (
            <Upload className="h-6 w-6 sm:h-8 sm:w-8" />
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-sm sm:text-base font-medium text-foreground">
            {isUploading
              ? 'Uploading signed declaration…'
              : isDragReject
                ? 'File type not allowed'
                : isDragActive
                  ? 'Drop files here'
                  : 'Upload signed declaration'}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {isUploading
              ? 'Please wait'
              : isDragActive
                ? 'Release to upload'
                : 'Drag & drop or click to browse (multiple files allowed)'}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (max 10MB)
          </p>
        </div>
      </div>
    </div>
  );
}
